/**
 * AdminPage - Admin dashboard with metrics and system health
 *
 * Features:
 * - System pulse (health indicators)
 * - Key metrics overview
 * - Ticker following stats
 * - Message/chat stats
 * - User funnel
 * - Recent activity
 * - Entity browser with drill-down
 */
import React, { useState, useEffect } from 'react';
import { YStack, XStack, H1, H2, H3, Text, ScrollView } from 'tamagui';
import { GlassCard, MatrixButton } from '../components/base';
import axios from 'axios';

interface AdminPageProps {
  accessToken: string | null;
}

// BASE Protocol entity format
interface BaseEntity {
  id: string;
  type: string;
  status: string;
  variant: string;
  tags: Record<string, any>;
  details: Record<string, any>;
  connectors: Record<string, string>;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

interface EntityBrowserData {
  data: BaseEntity[];
  pagination: {
    total_items: number;
    total_pages: number;
    current_page: number;
    page_size: number;
    has_next_page: boolean;
    has_previous_page: boolean;
  };
}

interface DashboardData {
  success: boolean;
  generated_at?: string;
  pulse?: {
    database: 'green' | 'yellow' | 'red';
    user_activity: 'green' | 'yellow' | 'red';
    content_generation: 'green' | 'yellow' | 'red';
  };
  summary?: {
    overview: {
      total_users: number;
      total_follows: number;
      total_messages: number;
      waitlist_size: number;
      mrr: number;
    };
    engagement: {
      avg_follows_per_user: number;
      avg_messages_per_user: number;
      users_chatting: number;
    };
    funnel: {
      waitlist: number;
      converted: number;
      conversion_rate: number;
    };
  };
  entity_counts?: Record<string, number>;
  ticker_stats?: {
    total_follows: number;
    users_following: number;
    avg_follows_per_user: number;
    top_tickers: Array<{ symbol: string; followers: number }>;
    distribution: Record<string, number>;
  };
  message_stats?: {
    total_messages: number;
    users_chatting: number;
    avg_messages_per_user: number;
    daily_messages: Array<{ date: string; count: number }>;
  };
  subscription_stats?: {
    tiers: Record<string, number>;
    total_subscribers: number;
    paid_subscribers: number;
    mrr: number;
  };
  waitlist_stats?: {
    total_waitlist: number;
    converted: number;
    conversion_rate: number;
  };
  recent_activity?: Array<{
    type: string;
    created_at: string;
    user_id: string;
  }>;
  error?: string;
}

const API_URL = 'https://nings-api.beamer-labs.workers.dev';

const AdminPage: React.FC<AdminPageProps> = ({ accessToken }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Entity browser state
  const [selectedEntityType, setSelectedEntityType] = useState<string | null>(null);
  const [entityBrowserData, setEntityBrowserData] = useState<EntityBrowserData | null>(null);
  const [entityBrowserLoading, setEntityBrowserLoading] = useState(false);
  const [entityBrowserPage, setEntityBrowserPage] = useState(1);
  const [selectedEntity, setSelectedEntity] = useState<BaseEntity | null>(null);

  // User/Ticker detail state
  const [userDetailData, setUserDetailData] = useState<any>(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [tickerDetailData, setTickerDetailData] = useState<any>(null);
  const [tickerDetailLoading, setTickerDetailLoading] = useState(false);

  const loadDashboard = async () => {
    try {
      setError(null);

      if (!accessToken) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/admin/dashboard`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.data.success) {
        setDashboard(response.data);
      } else {
        setError(response.data.error || 'Failed to load dashboard');
      }
    } catch (err: any) {
      console.error('Failed to load admin dashboard:', err);
      if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else {
        setError(err.response?.data?.detail || 'Failed to load dashboard');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [accessToken]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadDashboard();
  };

  // Load entities for the browser
  const loadEntities = async (entityType: string, page: number = 1) => {
    if (!accessToken) return;

    setEntityBrowserLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/admin/entities/${entityType}?page=${page}&page_size=10`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      setEntityBrowserData(response.data);
      setEntityBrowserPage(page);
    } catch (err) {
      console.error('Failed to load entities:', err);
    } finally {
      setEntityBrowserLoading(false);
    }
  };

  // Handle clicking an entity type to browse
  const handleEntityTypeClick = (entityType: string) => {
    setSelectedEntityType(entityType);
    setSelectedEntity(null);
    loadEntities(entityType, 1);
  };

  // Handle clicking an entity to see details
  const handleEntityClick = (entity: BaseEntity) => {
    setSelectedEntity(entity);
  };

  // Close entity browser
  const handleCloseBrowser = () => {
    setSelectedEntityType(null);
    setEntityBrowserData(null);
    setSelectedEntity(null);
  };

  // Format value for display (handles objects, arrays, etc.)
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') {
      if (Object.keys(value).length === 0) return '-';
      return JSON.stringify(value);
    }
    return String(value);
  };

  // Load user activity when clicking a user connector
  const loadUserActivity = async (userId: string) => {
    if (!accessToken) return;

    setUserDetailLoading(true);
    setTickerDetailData(null); // Clear ticker detail
    try {
      const response = await axios.get(
        `${API_URL}/admin/users/${userId}/activity?page=1&page_size=20`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      setUserDetailData(response.data);
    } catch (err) {
      console.error('Failed to load user activity:', err);
    } finally {
      setUserDetailLoading(false);
    }
  };

  // Load ticker details when clicking a ticker connector
  const loadTickerDetail = async (tickerId: string) => {
    if (!accessToken) return;

    setTickerDetailLoading(true);
    setUserDetailData(null); // Clear user detail
    try {
      const [followersRes, messagesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/tickers/${tickerId}/followers?page=1&page_size=10`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        axios.get(`${API_URL}/admin/tickers/${tickerId}/messages?page=1&page_size=10`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      ]);
      setTickerDetailData({
        ticker: followersRes.data.ticker,
        followers: followersRes.data,
        messages: messagesRes.data,
      });
    } catch (err) {
      console.error('Failed to load ticker details:', err);
    } finally {
      setTickerDetailLoading(false);
    }
  };

  // Close detail panels
  const closeDetailPanels = () => {
    setUserDetailData(null);
    setTickerDetailData(null);
  };

  const getPulseColor = (status: 'green' | 'yellow' | 'red' | undefined) => {
    switch (status) {
      case 'green':
        return '$green10';
      case 'yellow':
        return '$yellow10';
      case 'red':
        return '$red10';
      default:
        return '$colorPress';
    }
  };

  const getPulseIcon = (status: 'green' | 'yellow' | 'red' | undefined) => {
    switch (status) {
      case 'green':
        return '●';
      case 'yellow':
        return '●';
      case 'red':
        return '●';
      default:
        return '○';
    }
  };

  if (loading) {
    return (
      <YStack
        flex={1}
        padding="$4"
        gap="$6"
        maxWidth={1200}
        marginHorizontal="auto"
        width="100%"
        justifyContent="center"
        alignItems="center"
      >
        <Text color="$green10" fontFamily="$mono" fontSize="$5">
          Loading admin dashboard...
        </Text>
      </YStack>
    );
  }

  if (error) {
    return (
      <YStack
        flex={1}
        padding="$4"
        gap="$6"
        maxWidth={1200}
        marginHorizontal="auto"
        width="100%"
      >
        <YStack gap="$2" alignItems="center" paddingVertical="$4">
          <H1 color="$red10" fontFamily="$heading" textAlign="center">
            Access Denied
          </H1>
          <Text color="$colorPress" fontFamily="$body" fontSize="$4" textAlign="center">
            {error}
          </Text>
        </YStack>
      </YStack>
    );
  }

  const { pulse, summary, entity_counts, ticker_stats, message_stats, recent_activity } =
    dashboard || {};

  return (
    <YStack
      flex={1}
      padding="$4"
      gap="$6"
      maxWidth={1200}
      marginHorizontal="auto"
      width="100%"
    >
      {/* Header */}
      <XStack justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="$4">
        <YStack gap="$2">
          <H1 color="$cyan10" fontFamily="$heading">
            Admin Dashboard
          </H1>
          <Text color="$colorPress" fontFamily="$mono" fontSize="$2">
            Last updated: {dashboard?.generated_at ? new Date(dashboard.generated_at).toLocaleString() : 'N/A'}
          </Text>
        </YStack>
        <MatrixButton variant="secondary" onPress={handleRefresh} disabled={refreshing}>
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </MatrixButton>
      </XStack>

      {/* System Pulse */}
      <GlassCard borderColor="$cyan10" borderWidth={1}>
        <YStack gap="$3">
          <H2 color="$cyan10" fontFamily="$heading" fontSize="$5">
            System Pulse
          </H2>
          <XStack gap="$6" flexWrap="wrap">
            <XStack gap="$2" alignItems="center">
              <Text color={getPulseColor(pulse?.database)} fontSize="$6">
                {getPulseIcon(pulse?.database)}
              </Text>
              <Text color="$color" fontFamily="$body" fontSize="$3">
                Database
              </Text>
            </XStack>
            <XStack gap="$2" alignItems="center">
              <Text color={getPulseColor(pulse?.user_activity)} fontSize="$6">
                {getPulseIcon(pulse?.user_activity)}
              </Text>
              <Text color="$color" fontFamily="$body" fontSize="$3">
                User Activity
              </Text>
            </XStack>
            <XStack gap="$2" alignItems="center">
              <Text color={getPulseColor(pulse?.content_generation)} fontSize="$6">
                {getPulseIcon(pulse?.content_generation)}
              </Text>
              <Text color="$color" fontFamily="$body" fontSize="$3">
                Content Generation
              </Text>
            </XStack>
          </XStack>
        </YStack>
      </GlassCard>

      {/* Key Metrics */}
      <XStack gap="$4" flexWrap="wrap">
        <GlassCard flex={1} minWidth={200}>
          <YStack gap="$2" alignItems="center">
            <Text color="$green10" fontFamily="$heading" fontSize="$8">
              {summary?.overview.total_users || 0}
            </Text>
            <Text color="$colorPress" fontFamily="$body" fontSize="$3">
              Active Users
            </Text>
          </YStack>
        </GlassCard>
        <GlassCard flex={1} minWidth={200}>
          <YStack gap="$2" alignItems="center">
            <Text color="$green10" fontFamily="$heading" fontSize="$8">
              {summary?.overview.total_follows || 0}
            </Text>
            <Text color="$colorPress" fontFamily="$body" fontSize="$3">
              Tickers Followed
            </Text>
          </YStack>
        </GlassCard>
        <GlassCard flex={1} minWidth={200}>
          <YStack gap="$2" alignItems="center">
            <Text color="$green10" fontFamily="$heading" fontSize="$8">
              {summary?.overview.total_messages || 0}
            </Text>
            <Text color="$colorPress" fontFamily="$body" fontSize="$3">
              Messages
            </Text>
          </YStack>
        </GlassCard>
        <GlassCard flex={1} minWidth={200}>
          <YStack gap="$2" alignItems="center">
            <Text color="$green10" fontFamily="$heading" fontSize="$8">
              ${summary?.overview.mrr?.toFixed(2) || '0.00'}
            </Text>
            <Text color="$colorPress" fontFamily="$body" fontSize="$3">
              MRR
            </Text>
          </YStack>
        </GlassCard>
      </XStack>

      {/* Engagement Stats */}
      <XStack gap="$4" flexWrap="wrap">
        <GlassCard flex={1} minWidth={300}>
          <YStack gap="$4">
            <H2 color="$green10" fontFamily="$heading" fontSize="$5">
              Engagement
            </H2>
            <YStack gap="$3">
              <XStack justifyContent="space-between">
                <Text color="$colorPress" fontFamily="$body">
                  Avg follows per user
                </Text>
                <Text color="$color" fontFamily="$mono" fontWeight="600">
                  {summary?.engagement.avg_follows_per_user?.toFixed(1) || 0}
                </Text>
              </XStack>
              <XStack justifyContent="space-between">
                <Text color="$colorPress" fontFamily="$body">
                  Avg messages per user
                </Text>
                <Text color="$color" fontFamily="$mono" fontWeight="600">
                  {summary?.engagement.avg_messages_per_user?.toFixed(1) || 0}
                </Text>
              </XStack>
              <XStack justifyContent="space-between">
                <Text color="$colorPress" fontFamily="$body">
                  Users chatting
                </Text>
                <Text color="$color" fontFamily="$mono" fontWeight="600">
                  {summary?.engagement.users_chatting || 0}
                </Text>
              </XStack>
            </YStack>
          </YStack>
        </GlassCard>

        <GlassCard flex={1} minWidth={300}>
          <YStack gap="$4">
            <H2 color="$green10" fontFamily="$heading" fontSize="$5">
              User Funnel
            </H2>
            <YStack gap="$3">
              <XStack justifyContent="space-between">
                <Text color="$colorPress" fontFamily="$body">
                  Waitlist
                </Text>
                <Text color="$color" fontFamily="$mono" fontWeight="600">
                  {summary?.funnel.waitlist || 0}
                </Text>
              </XStack>
              <XStack justifyContent="space-between">
                <Text color="$colorPress" fontFamily="$body">
                  Converted
                </Text>
                <Text color="$color" fontFamily="$mono" fontWeight="600">
                  {summary?.funnel.converted || 0}
                </Text>
              </XStack>
              <XStack justifyContent="space-between">
                <Text color="$colorPress" fontFamily="$body">
                  Conversion rate
                </Text>
                <Text color="$color" fontFamily="$mono" fontWeight="600">
                  {summary?.funnel.conversion_rate?.toFixed(1) || 0}%
                </Text>
              </XStack>
            </YStack>
          </YStack>
        </GlassCard>
      </XStack>

      {/* Top Tickers */}
      <GlassCard>
        <YStack gap="$4">
          <H2 color="$green10" fontFamily="$heading" fontSize="$5">
            Top Followed Tickers
          </H2>
          {ticker_stats?.top_tickers && ticker_stats.top_tickers.length > 0 ? (
            <XStack gap="$3" flexWrap="wrap">
              {ticker_stats.top_tickers.map((ticker, index) => (
                <GlassCard
                  key={ticker.symbol}
                  backgroundColor="$backgroundHover"
                  padding="$3"
                  minWidth={100}
                >
                  <YStack alignItems="center" gap="$1">
                    <Text color="$green10" fontFamily="$mono" fontWeight="600" fontSize="$4">
                      {ticker.symbol}
                    </Text>
                    <Text color="$colorPress" fontFamily="$body" fontSize="$2">
                      {ticker.followers} followers
                    </Text>
                  </YStack>
                </GlassCard>
              ))}
            </XStack>
          ) : (
            <Text color="$colorPress" fontFamily="$body">
              No ticker data yet
            </Text>
          )}
        </YStack>
      </GlassCard>

      {/* Entity Counts - Clickable to browse */}
      <GlassCard>
        <YStack gap="$4">
          <H2 color="$green10" fontFamily="$heading" fontSize="$5">
            Entity Browser
          </H2>
          <Text color="$colorPress" fontFamily="$body" fontSize="$2">
            Click an entity type to browse and inspect
          </Text>
          {entity_counts && Object.keys(entity_counts).length > 0 ? (
            <XStack gap="$3" flexWrap="wrap">
              {Object.entries(entity_counts).map(([type, count]) => (
                <GlassCard
                  key={type}
                  backgroundColor={selectedEntityType === type ? '$green10' : '$backgroundHover'}
                  padding="$3"
                  minWidth={120}
                  cursor="pointer"
                  hoverStyle={{ backgroundColor: '$backgroundPress' }}
                  pressStyle={{ scale: 0.98 }}
                  onPress={() => handleEntityTypeClick(type)}
                >
                  <YStack alignItems="center" gap="$1">
                    <Text
                      color={selectedEntityType === type ? '$background' : '$green10'}
                      fontFamily="$mono"
                      fontWeight="600"
                      fontSize="$5"
                    >
                      {count}
                    </Text>
                    <Text
                      color={selectedEntityType === type ? '$background' : '$colorPress'}
                      fontFamily="$body"
                      fontSize="$2"
                    >
                      {type}
                    </Text>
                  </YStack>
                </GlassCard>
              ))}
            </XStack>
          ) : (
            <Text color="$colorPress" fontFamily="$body">
              No entities yet
            </Text>
          )}
        </YStack>
      </GlassCard>

      {/* Entity Browser Panel */}
      {selectedEntityType && (
        <GlassCard borderColor="$cyan10" borderWidth={1}>
          <YStack gap="$4">
            <XStack justifyContent="space-between" alignItems="center">
              <YStack>
                <H3 color="$cyan10" fontFamily="$heading" fontSize="$5">
                  {selectedEntityType}
                </H3>
                {entityBrowserData && (
                  <Text color="$colorPress" fontFamily="$mono" fontSize="$2">
                    Page {entityBrowserData.pagination.current_page} of {entityBrowserData.pagination.total_pages}
                    {' '}({entityBrowserData.pagination.total_items} total)
                  </Text>
                )}
              </YStack>
              <MatrixButton variant="secondary" size="$2" onPress={handleCloseBrowser}>
                Close
              </MatrixButton>
            </XStack>

            {entityBrowserLoading ? (
              <Text color="$green10" fontFamily="$mono">Loading entities...</Text>
            ) : entityBrowserData?.data && entityBrowserData.data.length > 0 ? (
              <YStack gap="$3">
                {/* Entity List */}
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <YStack gap="$2" minWidth={800}>
                    {/* Header */}
                    <XStack
                      gap="$3"
                      padding="$2"
                      backgroundColor="$backgroundPress"
                      borderRadius="$2"
                    >
                      <Text color="$cyan10" fontFamily="$mono" fontSize="$2" width={280}>
                        ID
                      </Text>
                      <Text color="$cyan10" fontFamily="$mono" fontSize="$2" width={100}>
                        Status
                      </Text>
                      <Text color="$cyan10" fontFamily="$mono" fontSize="$2" width={200}>
                        Connectors
                      </Text>
                      <Text color="$cyan10" fontFamily="$mono" fontSize="$2" width={180}>
                        Created
                      </Text>
                    </XStack>

                    {/* Rows */}
                    {entityBrowserData.data.map((entity, index) => (
                      <XStack
                        key={entity.id}
                        gap="$3"
                        padding="$2"
                        backgroundColor={
                          selectedEntity?.id === entity.id
                            ? '$green10'
                            : index % 2 === 0
                            ? '$backgroundHover'
                            : 'transparent'
                        }
                        borderRadius="$2"
                        cursor="pointer"
                        hoverStyle={{ backgroundColor: '$backgroundPress' }}
                        onPress={() => handleEntityClick(entity)}
                      >
                        <Text
                          color={selectedEntity?.id === entity.id ? '$background' : '$color'}
                          fontFamily="$mono"
                          fontSize="$2"
                          width={280}
                        >
                          {entity.id}
                        </Text>
                        <Text
                          color={selectedEntity?.id === entity.id ? '$background' : '$colorPress'}
                          fontFamily="$mono"
                          fontSize="$2"
                          width={100}
                        >
                          {entity.status}
                        </Text>
                        <Text
                          color={selectedEntity?.id === entity.id ? '$background' : '$colorPress'}
                          fontFamily="$mono"
                          fontSize="$2"
                          width={200}
                          numberOfLines={1}
                        >
                          {Object.entries(entity.connectors || {})
                            .map(([k, v]) => `${k}: ${v?.toString?.()?.substring(0, 8) ?? v}...`)
                            .join(', ') || '-'}
                        </Text>
                        <Text
                          color={selectedEntity?.id === entity.id ? '$background' : '$colorPress'}
                          fontFamily="$mono"
                          fontSize="$2"
                          width={180}
                        >
                          {new Date(entity.created_at).toLocaleString()}
                        </Text>
                      </XStack>
                    ))}
                  </YStack>
                </ScrollView>

                {/* Pagination */}
                <XStack justifyContent="center" gap="$3">
                  <MatrixButton
                    variant="secondary"
                    size="$2"
                    disabled={!entityBrowserData.pagination.has_previous_page}
                    onPress={() => loadEntities(selectedEntityType, entityBrowserPage - 1)}
                  >
                    Previous
                  </MatrixButton>
                  <MatrixButton
                    variant="secondary"
                    size="$2"
                    disabled={!entityBrowserData.pagination.has_next_page}
                    onPress={() => loadEntities(selectedEntityType, entityBrowserPage + 1)}
                  >
                    Next
                  </MatrixButton>
                </XStack>
              </YStack>
            ) : (
              <Text color="$colorPress" fontFamily="$body">No entities found</Text>
            )}
          </YStack>
        </GlassCard>
      )}

      {/* Entity Detail Panel */}
      {selectedEntity && (
        <GlassCard borderColor="$yellow10" borderWidth={1}>
          <YStack gap="$4">
            <XStack justifyContent="space-between" alignItems="center">
              <H3 color="$yellow10" fontFamily="$heading" fontSize="$5">
                Entity Details
              </H3>
              <MatrixButton variant="secondary" size="$2" onPress={() => setSelectedEntity(null)}>
                Close
              </MatrixButton>
            </XStack>

            <YStack gap="$3">
              {/* Core Fields */}
              <YStack gap="$2">
                <Text color="$cyan10" fontFamily="$heading" fontSize="$3">Core Fields</Text>
                <XStack flexWrap="wrap" gap="$4">
                  <YStack minWidth={200}>
                    <Text color="$colorPress" fontFamily="$body" fontSize="$2">ID</Text>
                    <Text color="$color" fontFamily="$mono" fontSize="$2">{selectedEntity.id}</Text>
                  </YStack>
                  <YStack minWidth={100}>
                    <Text color="$colorPress" fontFamily="$body" fontSize="$2">Type</Text>
                    <Text color="$color" fontFamily="$mono" fontSize="$2">{selectedEntity.type}</Text>
                  </YStack>
                  <YStack minWidth={100}>
                    <Text color="$colorPress" fontFamily="$body" fontSize="$2">Status</Text>
                    <Text color="$color" fontFamily="$mono" fontSize="$2">{selectedEntity.status}</Text>
                  </YStack>
                  <YStack minWidth={100}>
                    <Text color="$colorPress" fontFamily="$body" fontSize="$2">Variant</Text>
                    <Text color="$color" fontFamily="$mono" fontSize="$2">{selectedEntity.variant}</Text>
                  </YStack>
                </XStack>
              </YStack>

              {/* Connectors - Clickable to drill down */}
              {Object.keys(selectedEntity.connectors || {}).length > 0 && (
                <YStack gap="$2">
                  <Text color="$cyan10" fontFamily="$heading" fontSize="$3">Connectors (click to explore)</Text>
                  <XStack flexWrap="wrap" gap="$4">
                    {Object.entries(selectedEntity.connectors).map(([key, value]) => (
                      <YStack
                        key={key}
                        minWidth={200}
                        cursor={key === 'user' || key === 'ticker' ? 'pointer' : 'default'}
                        hoverStyle={key === 'user' || key === 'ticker' ? { opacity: 0.8 } : {}}
                        onPress={() => {
                          if (key === 'user' && value) {
                            loadUserActivity(String(value));
                          } else if (key === 'ticker' && value) {
                            loadTickerDetail(String(value));
                          }
                        }}
                      >
                        <Text color="$colorPress" fontFamily="$body" fontSize="$2">{key}</Text>
                        <Text
                          color={key === 'user' || key === 'ticker' ? '$cyan10' : '$green10'}
                          fontFamily="$mono"
                          fontSize="$2"
                          textDecorationLine={key === 'user' || key === 'ticker' ? 'underline' : 'none'}
                        >
                          {formatValue(value)}
                        </Text>
                      </YStack>
                    ))}
                  </XStack>
                </YStack>
              )}

              {/* Tags (only show non-empty) */}
              {Object.entries(selectedEntity.tags || {}).filter(([_, v]) => v !== null && v !== undefined && (typeof v !== 'object' || Object.keys(v).length > 0)).length > 0 && (
                <YStack gap="$2">
                  <Text color="$cyan10" fontFamily="$heading" fontSize="$3">Tags</Text>
                  <XStack flexWrap="wrap" gap="$4">
                    {Object.entries(selectedEntity.tags)
                      .filter(([_, v]) => v !== null && v !== undefined && (typeof v !== 'object' || Object.keys(v).length > 0))
                      .map(([key, value]) => (
                        <YStack key={key} minWidth={150} maxWidth={300}>
                          <Text color="$colorPress" fontFamily="$body" fontSize="$2">{key}</Text>
                          <Text
                            color="$color"
                            fontFamily="$mono"
                            fontSize="$2"
                            wordWrap="break-word"
                          >
                            {formatValue(value)}
                          </Text>
                        </YStack>
                      ))}
                  </XStack>
                </YStack>
              )}

              {/* Details (only show non-empty) */}
              {Object.keys(selectedEntity.details || {}).length > 0 && (
                <YStack gap="$2">
                  <Text color="$cyan10" fontFamily="$heading" fontSize="$3">Details</Text>
                  <GlassCard backgroundColor="$backgroundHover" padding="$3">
                    <ScrollView style={{ maxHeight: 300 }}>
                      <Text
                        color="$color"
                        fontFamily="$mono"
                        fontSize="$2"
                        whiteSpace="pre-wrap"
                        wordWrap="break-word"
                      >
                        {JSON.stringify(selectedEntity.details, null, 2)}
                      </Text>
                    </ScrollView>
                  </GlassCard>
                </YStack>
              )}

              {/* Timestamps */}
              <YStack gap="$2">
                <Text color="$cyan10" fontFamily="$heading" fontSize="$3">Timestamps</Text>
                <XStack flexWrap="wrap" gap="$4">
                  <YStack minWidth={200}>
                    <Text color="$colorPress" fontFamily="$body" fontSize="$2">Created</Text>
                    <Text color="$color" fontFamily="$mono" fontSize="$2">
                      {new Date(selectedEntity.created_at).toLocaleString()}
                    </Text>
                  </YStack>
                  <YStack minWidth={200}>
                    <Text color="$colorPress" fontFamily="$body" fontSize="$2">Updated</Text>
                    <Text color="$color" fontFamily="$mono" fontSize="$2">
                      {new Date(selectedEntity.updated_at).toLocaleString()}
                    </Text>
                  </YStack>
                  <YStack minWidth={200}>
                    <Text color="$colorPress" fontFamily="$body" fontSize="$2">Created By</Text>
                    <Text color="$color" fontFamily="$mono" fontSize="$2">
                      {selectedEntity.created_by?.toString?.()?.substring(0, 8) ?? '-'}...
                    </Text>
                  </YStack>
                </XStack>
              </YStack>
            </YStack>
          </YStack>
        </GlassCard>
      )}

      {/* User Detail Panel */}
      {(userDetailLoading || userDetailData) && (
        <GlassCard borderColor="$purple10" borderWidth={1}>
          <YStack gap="$4">
            <XStack justifyContent="space-between" alignItems="center">
              <YStack>
                <H3 color="$purple10" fontFamily="$heading" fontSize="$5">
                  User Activity
                </H3>
                {userDetailData?.email && (
                  <Text color="$colorPress" fontFamily="$mono" fontSize="$2">
                    {userDetailData.email}
                  </Text>
                )}
              </YStack>
              <MatrixButton variant="secondary" size="$2" onPress={closeDetailPanels}>
                Close
              </MatrixButton>
            </XStack>

            {userDetailLoading ? (
              <Text color="$green10" fontFamily="$mono">Loading user activity...</Text>
            ) : userDetailData?.data && userDetailData.data.length > 0 ? (
              <YStack gap="$3">
                <Text color="$colorPress" fontFamily="$mono" fontSize="$2">
                  User ID: {userDetailData.user_id} | Total: {userDetailData.pagination?.total || userDetailData.data.length} entities
                </Text>
                <YStack gap="$2">
                  {userDetailData.data.map((entity: BaseEntity, index: number) => (
                    <XStack
                      key={entity.id}
                      gap="$3"
                      padding="$2"
                      backgroundColor={index % 2 === 0 ? '$backgroundHover' : 'transparent'}
                      borderRadius="$2"
                      cursor="pointer"
                      hoverStyle={{ backgroundColor: '$backgroundPress' }}
                      onPress={() => handleEntityClick(entity)}
                    >
                      <Text color="$cyan10" fontFamily="$mono" fontSize="$2" width={120}>
                        {entity.type}
                      </Text>
                      <Text color="$color" fontFamily="$mono" fontSize="$2" flex={1}>
                        {entity.id?.toString?.()?.substring(0, 12)}...
                      </Text>
                      <Text color="$colorPress" fontFamily="$mono" fontSize="$2">
                        {new Date(entity.created_at).toLocaleDateString()}
                      </Text>
                    </XStack>
                  ))}
                </YStack>
              </YStack>
            ) : (
              <Text color="$colorPress" fontFamily="$body">No activity found</Text>
            )}
          </YStack>
        </GlassCard>
      )}

      {/* Ticker Detail Panel */}
      {(tickerDetailLoading || tickerDetailData) && (
        <GlassCard borderColor="$orange10" borderWidth={1}>
          <YStack gap="$4">
            <XStack justifyContent="space-between" alignItems="center">
              <YStack>
                <H3 color="$orange10" fontFamily="$heading" fontSize="$5">
                  Ticker Details
                </H3>
                {tickerDetailData?.ticker?.tags?.symbol && (
                  <Text color="$green10" fontFamily="$mono" fontSize="$4" fontWeight="bold">
                    {tickerDetailData.ticker.tags.symbol}
                  </Text>
                )}
              </YStack>
              <MatrixButton variant="secondary" size="$2" onPress={closeDetailPanels}>
                Close
              </MatrixButton>
            </XStack>

            {tickerDetailLoading ? (
              <Text color="$green10" fontFamily="$mono">Loading ticker details...</Text>
            ) : tickerDetailData ? (
              <YStack gap="$4">
                {/* Ticker Info */}
                {tickerDetailData.ticker && (
                  <YStack gap="$2">
                    <Text color="$cyan10" fontFamily="$heading" fontSize="$3">Ticker Info</Text>
                    <XStack flexWrap="wrap" gap="$4">
                      {tickerDetailData.ticker.tags?.exchange && (
                        <YStack minWidth={100}>
                          <Text color="$colorPress" fontFamily="$body" fontSize="$2">Exchange</Text>
                          <Text color="$color" fontFamily="$mono" fontSize="$2">{tickerDetailData.ticker.tags.exchange}</Text>
                        </YStack>
                      )}
                      {tickerDetailData.ticker.tags?.sector && (
                        <YStack minWidth={100}>
                          <Text color="$colorPress" fontFamily="$body" fontSize="$2">Sector</Text>
                          <Text color="$color" fontFamily="$mono" fontSize="$2">{tickerDetailData.ticker.tags.sector}</Text>
                        </YStack>
                      )}
                      {tickerDetailData.ticker.tags?.industry && (
                        <YStack minWidth={150}>
                          <Text color="$colorPress" fontFamily="$body" fontSize="$2">Industry</Text>
                          <Text color="$color" fontFamily="$mono" fontSize="$2">{tickerDetailData.ticker.tags.industry}</Text>
                        </YStack>
                      )}
                    </XStack>
                  </YStack>
                )}

                {/* Followers */}
                <YStack gap="$2">
                  <Text color="$cyan10" fontFamily="$heading" fontSize="$3">
                    Followers ({tickerDetailData.followers?.pagination?.total || 0})
                  </Text>
                  {tickerDetailData.followers?.data?.length > 0 ? (
                    <YStack gap="$1">
                      {tickerDetailData.followers.data.slice(0, 5).map((follow: BaseEntity, idx: number) => (
                        <XStack
                          key={follow.id}
                          gap="$2"
                          padding="$1"
                          backgroundColor={idx % 2 === 0 ? '$backgroundHover' : 'transparent'}
                          borderRadius="$1"
                          cursor="pointer"
                          onPress={() => follow.connectors?.user && loadUserActivity(String(follow.connectors.user))}
                        >
                          <Text color="$cyan10" fontFamily="$mono" fontSize="$2" textDecorationLine="underline">
                            {follow.connectors?.user?.toString?.()?.substring(0, 12)}...
                          </Text>
                          <Text color="$colorPress" fontFamily="$mono" fontSize="$2">
                            followed {new Date(follow.created_at).toLocaleDateString()}
                          </Text>
                        </XStack>
                      ))}
                    </YStack>
                  ) : (
                    <Text color="$colorPress" fontFamily="$body" fontSize="$2">No followers</Text>
                  )}
                </YStack>

                {/* Messages */}
                <YStack gap="$2">
                  <Text color="$cyan10" fontFamily="$heading" fontSize="$3">
                    Messages ({tickerDetailData.messages?.pagination?.total || 0})
                  </Text>
                  {tickerDetailData.messages?.data?.length > 0 ? (
                    <YStack gap="$1">
                      {tickerDetailData.messages.data.slice(0, 5).map((msg: BaseEntity, idx: number) => (
                        <XStack
                          key={msg.id}
                          gap="$2"
                          padding="$1"
                          backgroundColor={idx % 2 === 0 ? '$backgroundHover' : 'transparent'}
                          borderRadius="$1"
                        >
                          <Text color="$colorPress" fontFamily="$mono" fontSize="$2" flex={1} numberOfLines={1}>
                            {msg.tags?.content?.toString?.()?.substring(0, 50) || msg.details?.content?.toString?.()?.substring(0, 50) || 'No content'}...
                          </Text>
                          <Text color="$colorPress" fontFamily="$mono" fontSize="$2">
                            {new Date(msg.created_at).toLocaleDateString()}
                          </Text>
                        </XStack>
                      ))}
                    </YStack>
                  ) : (
                    <Text color="$colorPress" fontFamily="$body" fontSize="$2">No messages</Text>
                  )}
                </YStack>
              </YStack>
            ) : null}
          </YStack>
        </GlassCard>
      )}

      {/* Recent Activity */}
      <GlassCard>
        <YStack gap="$4">
          <H2 color="$green10" fontFamily="$heading" fontSize="$5">
            Recent Activity
          </H2>
          {recent_activity && recent_activity.length > 0 ? (
            <YStack gap="$2">
              {recent_activity.map((activity, index) => (
                <XStack
                  key={index}
                  gap="$3"
                  padding="$2"
                  backgroundColor={index % 2 === 0 ? '$backgroundHover' : 'transparent'}
                  borderRadius="$2"
                >
                  <Text
                    color="$cyan10"
                    fontFamily="$mono"
                    fontSize="$2"
                    minWidth={120}
                  >
                    {activity.type}
                  </Text>
                  <Text color="$colorPress" fontFamily="$mono" fontSize="$2" flex={1}>
                    {activity.user_id?.substring(0, 8)}...
                  </Text>
                  <Text color="$colorPress" fontFamily="$mono" fontSize="$2">
                    {activity.created_at
                      ? new Date(activity.created_at).toLocaleString()
                      : 'N/A'}
                  </Text>
                </XStack>
              ))}
            </YStack>
          ) : (
            <Text color="$colorPress" fontFamily="$body">
              No recent activity
            </Text>
          )}
        </YStack>
      </GlassCard>

      {/* Daily Messages Chart (simple text representation) */}
      {message_stats?.daily_messages && message_stats.daily_messages.length > 0 && (
        <GlassCard>
          <YStack gap="$4">
            <H2 color="$green10" fontFamily="$heading" fontSize="$5">
              Daily Messages (Last 14 Days)
            </H2>
            <YStack gap="$2">
              {message_stats.daily_messages.map((day) => (
                <XStack key={day.date} gap="$3" alignItems="center">
                  <Text color="$colorPress" fontFamily="$mono" fontSize="$2" minWidth={100}>
                    {day.date}
                  </Text>
                  <XStack
                    backgroundColor="$green10"
                    height={16}
                    width={Math.max(4, day.count * 10)}
                    borderRadius="$1"
                  />
                  <Text color="$color" fontFamily="$mono" fontSize="$2">
                    {day.count}
                  </Text>
                </XStack>
              ))}
            </YStack>
          </YStack>
        </GlassCard>
      )}
    </YStack>
  );
};

export default AdminPage;
